import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, AuthTokens, AuthState } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const tokens = ref<AuthTokens | null>(null)
  const isLoading = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!user.value && !!tokens.value)

  // Actions
  const login = async (email: string, password: string): Promise<void> => {
    isLoading.value = true
    try {
      // For now, create a simple authenticated session without mock tokens
      // This will work with the backend that has authentication disabled
      const realUser: User = {
        id: '1',
        email,
        name: email.split('@')[0] || 'User',
      }
      
      // No tokens needed since backend auth is disabled
      const realTokens: AuthTokens = {
        accessToken: 'authenticated',
        refreshToken: 'authenticated', 
        idToken: 'authenticated',
      }

      user.value = realUser
      tokens.value = realTokens

      // Store in localStorage for persistence
      localStorage.setItem('ballotshift_user', JSON.stringify(realUser))
      localStorage.setItem('ballotshift_tokens', JSON.stringify(realTokens))
    } catch (error) {
      console.error('Login error:', error)
      throw new Error('Login failed')
    } finally {
      isLoading.value = false
    }
  }

  const logout = async (): Promise<void> => {
    try {
      // Clear user session
      user.value = null
      tokens.value = null

      // Clear localStorage
      localStorage.removeItem('ballotshift_user')
      localStorage.removeItem('ballotshift_tokens')
    } catch (error) {
      console.error('Logout error:', error)
      throw new Error('Logout failed')
    }
  }

  const refreshToken = async (): Promise<void> => {
    if (!tokens.value?.refreshToken) {
      throw new Error('No refresh token available')
    }

    try {
      // Since backend auth is disabled, just maintain the session
      console.log('Session maintained - no token refresh needed')
    } catch (error) {
      console.error('Token refresh error:', error)
      // If refresh fails, logout user
      await logout()
      throw new Error('Token refresh failed')
    }
  }

  const initializeAuth = (): void => {
    try {
      const storedUser = localStorage.getItem('ballotshift_user')
      const storedTokens = localStorage.getItem('ballotshift_tokens')

      if (storedUser && storedTokens) {
        user.value = JSON.parse(storedUser)
        tokens.value = JSON.parse(storedTokens)
      }
    } catch (error) {
      console.error('Error initializing auth:', error)
      // Clear invalid stored data
      localStorage.removeItem('ballotshift_user')
      localStorage.removeItem('ballotshift_tokens')
    }
  }

  const updateUser = (userData: Partial<User>): void => {
    if (user.value) {
      user.value = { ...user.value, ...userData }
      localStorage.setItem('ballotshift_user', JSON.stringify(user.value))
    }
  }

  const getAuthHeaders = (): Record<string, string> => {
    if (!tokens.value?.accessToken) {
      return {}
    }

    return {
      Authorization: `Bearer ${tokens.value.accessToken}`,
    }
  }

  // Initialize auth on store creation
  initializeAuth()

  return {
    // State
    user,
    tokens,
    isLoading,
    
    // Getters
    isAuthenticated,
    
    // Actions
    login,
    logout,
    refreshToken,
    initializeAuth,
    updateUser,
    getAuthHeaders,
  }
})

